import "./OcipError.css";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Button, Table, Modal } from "react-bootstrap";
import { getIvFieldValue } from "../../utils/ivErrorDataUtils";

/**
 * 동이 인버터 표시 규칙(백엔드 기준)
 * - state: 운전상태 (0=정지, 1=운전)
 * - Pfault: 준비/고장 코드 (00=준비, 1~99=고장 코드)
 *
 * 여기서는 "상세 페이지 없이 번호만" 보여주는 컨셉이라
 * fault table은 만들지 않고, 현재 값만 표시 + 코드 목록(00~99) 정도만 보여줌.
 */

const DONGEI_RUN_STATE = {
  0: "정지(Stop)",
  1: "운전(Run)",
};

function normalizeInt(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pad2(n) {
  if (n === null || n === undefined) return "N/A";
  const x = Number(n);
  if (!Number.isFinite(x)) return "N/A";
  return String(Math.trunc(x)).padStart(2, "0");
}

const DongeiError = ({ SelInverter }) => {
  const ivData = useSelector((state) => state.ivData || {});

  const [openModal, setOpenModal] = useState(null); // "FAULT" | "RUN" | null
  const closeModal = () => setOpenModal(null);

  const getFirstFieldValue = (keys) => {
    for (const key of keys) {
      const value = getIvFieldValue(ivData, key, SelInverter, null);
      if (value !== null) return value;
    }
    return null;
  };

  // ✅ 동이: Pfault / state 를 우선으로 읽고, 혹시 다른 키로 올 수도 있으니 fallback
  const faultCode = normalizeInt(
    getFirstFieldValue(["Pfault", "pfault", "fault", "fault_code", "alarm_code"])
  );

  const runState = normalizeInt(
    getFirstFieldValue(["state", "run_state", "Status", "status"])
  );

  // fault: 00(=0)이면 정상(준비), 1~99면 이상
  const isFaultAbnormal = faultCode != null && Number(faultCode) !== 0;

  // runState: 0/1 이외는 이상으로 표시
  const isRunAbnormal =
    runState != null && !(runState in DONGEI_RUN_STATE);

  const runLabel =
    runState == null
      ? null
      : DONGEI_RUN_STATE[runState] ?? `알 수 없음 (${runState})`;

  const renderModalContent = () => {
    if (openModal === "FAULT") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Fault 코드</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 코드:</b>{" "}
                {faultCode == null ? "N/A" : pad2(faultCode)}
              </div>
              <div>
                <b>판정:</b>{" "}
                {faultCode == null
                  ? "N/A"
                  : Number(faultCode) === 0
                  ? "준비(정상)"
                  : "고장(Fault)"}
              </div>
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 8 }}>
                * 동이는 인버터 종류에 따라 상세 의미가 달라 사무실로 문의 주시기 바랍니다. 
              </div>
            </div>

            {/* 상세 테이블을 안 만든다고 했으니, 코드 범위 안내용으로만 간단히 */}
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>코드 범위</th>
                  <th>의미</th>
                </tr>
              </thead>
              <tbody>
                <tr className={faultCode === 0 ? "active-row" : ""}>
                  <td>00</td>
                  <td>준비(Ready)</td>
                </tr>
                <tr className={faultCode != null && faultCode > 0 ? "active-row" : ""}>
                  <td>01 ~ 99</td>
                  <td>Fault 코드(장비별 상세 의미 상이)</td>
                </tr>
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    if (openModal === "RUN") {
      return (
        <>
          <Modal.Header closeButton>
            <Modal.Title>운전상태</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12 }}>
              <div>
                <b>현재 코드:</b>{" "}
                {runState == null ? "N/A" : runState}
              </div>
              <div>
                <b>의미:</b>{" "}
                {runState == null ? "N/A" : runLabel}
              </div>
            </div>

            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>코드</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(DONGEI_RUN_STATE)
                  .map((k) => Number(k))
                  .sort((a, b) => a - b)
                  .map((code) => {
                    const isActive = runState === code;
                    return (
                      <tr key={code} className={isActive ? "active-row" : ""}>
                        <td>{code}</td>
                        <td>{DONGEI_RUN_STATE[code]}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
          </Modal.Footer>
        </>
      );
    }

    return null;
  };

  return (
    <div className="status-list-container">
      <div className="status-list">
        {/* 1) Fault 코드 */}
        <div className="status-item">
          <span className="status-value">
            Fault 코드: {faultCode == null ? "N/A" : pad2(faultCode)}
            {"  "}
            {faultCode == null
              ? ""
              : Number(faultCode) === 0
              ? "(준비)"
              : "(Fault)"}
          </span>
          <button onClick={() => setOpenModal("FAULT")}>
            <span className={isFaultAbnormal ? "blinking-icon" : ""}>
              {isFaultAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

        {/* 2) 운전상태 */}
        <div className="status-item">
          <span className="status-value">
            운전상태: {runState == null ? "N/A" : runState}
            {"  "}
            {runState == null ? "" : `(${runLabel})`}
          </span>
          <button onClick={() => setOpenModal("RUN")}>
            <span className={isRunAbnormal ? "blinking-icon" : ""}>
              {isRunAbnormal ? "🔴" : "🟢"}
            </span>
          </button>
        </div>

      </div>

      {openModal !== null && (
        <Modal show onHide={closeModal} size="lg">
          {renderModalContent()}
        </Modal>
      )}
    </div>
  );
};

export default DongeiError;
